import { useTranslation } from "react-i18next";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { startCase } from "lodash";
import { ArrowRight, User } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardCard } from "@/components/common/DashboardCard";
import type { IEmployee } from "@/types/(company)/userDashboard.interface";
import { useGetRecentEmployeesQuery } from "@/services/(company)/userApi";
import { ROUTES } from "@/constants/routes";
import { PATHS } from "@/routes/paths";
import { useLocale } from "@/hooks/useLocale";

const COL_WIDTHS = ["30%", "20%", "20%", "15%", "12%", "56px"];

interface IProps {
  /**
   * Lets the caller own the card's height. The dashboard splits its page into a
   * widget half and a fixed half, so the table has to fill whatever the fixed
   * half is rather than the standalone 520px it uses on its own.
   */
  className?: string;
}

const RowSkeleton = () => (
  <tr className="border-b border-border">
    <td className="py-3 px-4">
      <div className="flex items-center gap-2.5">
        <Skeleton className="size-8 rounded-full shrink-0" />
        <Skeleton className="h-3.5 w-28 rounded" />
      </div>
    </td>
    <td className="py-3 px-4">
      <Skeleton className="h-3 w-20 rounded" />
    </td>
    <td className="py-3 px-4">
      <Skeleton className="h-3 w-24 rounded" />
    </td>
    <td className="py-3 px-4">
      <Skeleton className="h-3 w-20 rounded" />
    </td>
    <td className="py-3 px-4">
      <Skeleton className="h-5 w-16 rounded-full" />
    </td>
    <td className="py-3 px-4"></td>
  </tr>
);

const RecentEmployeesTable: React.FC<IProps> = ({
  className = "h-[520px]!",
}) => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const { data, isLoading } = useGetRecentEmployeesQuery({ limit: 10 });
  const recentEmployees = data?.data || [];

  const getDetailPath = (employee: IEmployee) =>
    PATHS.COMPANY.COMPANY_EMPLOYEES_PATHS.ONBOARDING + "?id=" + employee.userId;

  const getStatusBadge = (status: string) => (
    <Badge variant={statusToVariant(status)} dot={false}>
      {t(`common:status.${status.toLowerCase()}`, {
        defaultValue: startCase(status),
      })}
    </Badge>
  );

  return (
    <DashboardCard
      title={t("company:dashboard.recent_employees")}
      action={{
        label: t("company:dashboard.view_all_employees"),
        link: ROUTES.HOME.EMPLOYEES.ROOT,
      }}
      contentClassName="p-0! pt-0! flex flex-col overflow-hidden!"
      rootClassname={className}
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-x-auto scrollbar-thin">
        {/* Fixed header */}
        <table className="w-full min-w-[640px] border-collapse table-fixed">
          <colgroup>
            {COL_WIDTHS.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-muted border-y border-border">
              {[
                t("common:labels.employee"),
                t("company:dashboard.position"),
                t("common:labels.department"),
                t("company:dashboard.start_date"),
                t("common:labels.status"),
                "",
              ].map((h, i) => (
                <th
                  key={i}
                  className="text-start text-[11px] font-semibold text-muted-foreground uppercase tracking-wider py-3 px-4"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        </table>

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin">
          <table className="w-full min-w-[640px] border-collapse table-fixed">
            <colgroup>
              {COL_WIDTHS.map((w, i) => (
                <col key={i} style={{ width: w }} />
              ))}
            </colgroup>
            <tbody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <RowSkeleton key={i} />
                ))
              ) : !recentEmployees.length ? (
                <tr>
                  <td
                    colSpan={6}
                    className="py-16 text-center text-sm text-muted-foreground"
                  >
                    {t("company:dashboard.no_recent_employees_found")}
                  </td>
                </tr>
              ) : (
                recentEmployees.map((employee) => (
                  <tr
                    key={employee.userId}
                    onClick={() => navigate(getDetailPath(employee))}
                    className="border-b border-border last:border-0 hover:bg-muted transition-colors cursor-pointer group"
                  >
                    <td className="py-3.5 px-4">
                      <Link
                        to={getDetailPath(employee)}
                        className="flex items-center gap-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Avatar className="size-8 shrink-0 ring-2 ring-border ring-offset-1">
                          <AvatarImage
                            src={employee.profileImage ?? undefined}
                          />
                          <AvatarFallback className="bg-muted text-muted-foreground">
                            <User size={14} />
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                          {employee.fullName}
                        </span>
                      </Link>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="text-xs text-muted-foreground">
                        {employee.position || "—"}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="text-xs text-muted-foreground">
                        {employee.department || "—"}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="text-xs text-muted-foreground">
                        {employee.startDate
                          ? formatDate(
                              new Date(employee.startDate),
                              "dd MMM yyyy",
                            )
                          : "—"}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      {getStatusBadge(employee.status)}
                    </td>
                    <td className="py-3.5 px-2">
                      <div className="flex justify-center">
                        <ArrowRight
                          size={14}
                          className="rtl:rotate-180 text-muted-foreground group-hover:text-primary transition-colors"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardCard>
  );
};

export { RecentEmployeesTable };
